import React, { useRef } from 'react'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import { useTranslation } from 'react-i18next'
import styles from 'components/Shared/Dialogs/WarningDialog/WarningDialog.module.css'
import DialogTitle from '@material-ui/core/DialogTitle'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { ContainedButton } from 'components/Shared/ContainedButton'
import { PrimaryButton } from 'components/Shared/PrimaryButton'
import { ContractorFormFields, ContractorSchema } from 'components/Contractors/ContractorFormFields'
import { createContractor } from 'services/ContractorService'
import { HTTP_404_ERROR, HTTP_422_ERROR } from 'Constants'
import ApiError from 'constants/ApiError'
import { initialValues } from 'components/Contractors/Create/CreateContractorForm'
import { useErrorHandling } from 'context/errorHandling'
import { triggerFormSubmit } from 'utils/utils'
import { FormikForm } from 'components/Shared/FormikForm'
import Module from 'constants/customFieldModules'

const useStyles = makeStyles({
  paper: {
    padding: '15px 30px 32px'
  }
})

export function CreateContractorDialog ({ open, newContractorName, onClose, requiredRoles }) {
  const classes = useStyles()
  const { t } = useTranslation()
  const { handleError } = useErrorHandling()

  const createContractorFormRef = useRef()

  const closeWithoutResult = () => onClose(false)

  function capitalize (str) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  function handleSubmit (values, onSuccess) {
    createContractor(values)
      .then(createdContractor => {
          onClose(createdContractor)
          return onSuccess(createdContractor.id)
            .catch(handleError)
        }
      )
      .catch(error => {
        const errorCode = error?.response?.data?.errorCode
        handleError(error, {
          [HTTP_404_ERROR]: 'notifications.error.contractorNotFound',
          [HTTP_422_ERROR]: errorCode === 'CONTRACTOR_NIP_CONFLICT' ?
            'notifications.error.contractorNipNumberAlreadyExists' :
            errorCode === 'CONTRACTOR_CONTACT_EMAIL_CONFLICT' ?
              'notifications.error.contractorContactEmailAlreadyExists' :
            errorCode === 'CONTRACTOR_NAME_CONFLICT' ?
              'notifications.error.contractorFullNameAlreadyExists' :
            ApiError.UnprocessableEntity
        })
      })
  }

  return (
    <Dialog
      classes={{ paper: classes.paper }}
      open={open}
      maxWidth={'xl'}
      onClose={closeWithoutResult}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle className={styles.titleContainer}
                   id="alert-dialog-title">{t('contractor.form.createTitle')}</DialogTitle>
      <DialogContent>
        <FormikForm
          innerRef={createContractorFormRef}
          initialValues={{
            ...initialValues,
            fullName: newContractorName && capitalize(newContractorName),
            roles: requiredRoles
          }}
          validationSchema={ContractorSchema}
          onSubmit={handleSubmit}
          customFields={{
            module: Module.Contractors
          }}>
          {() => (
            <ContractorFormFields requiredRoles={requiredRoles} initialFiles={[]}/>
          )}
        </FormikForm>
      </DialogContent>
      <DialogActions
        className={styles.dialogActions}
        disableSpacing
      >
        <div className={styles.dialogButton}>
          <PrimaryButton
            onClick={closeWithoutResult}
            label={'notifications.back'}
          />
        </div>
        <div className={styles.dialogButton}>
          <ContainedButton
            className={styles.dialogButton}
            onClick={triggerFormSubmit(createContractorFormRef)}
            label={'notifications.confirm'}
          />
        </div>
      </DialogActions>
    </Dialog>
  )
}
